import { useEffect, useState } from "react";
import { UserRoundSearch } from "lucide-react";
import client from "../api/client";
import Modal from "../Moodboard/Modal";
import { GSTIN, STATE_OPTIONS } from "./gst";

const EMPTY = {
  name: "", company: "", gstin: "", phone: "", email: "", billing_address: "", shipping_address: "", state_code: "", notes: "", source_user_id: null,
};

// Copies a registered portal user's details into the form; the customer stays a
// separate record.
function PortalUserPicker({ onPick, onClose }) {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    client
      .get("/api/admin/users", { params: { status: "approved" } })
      .then(({ data }) => setUsers(data.items || []))
      .catch(() => setUsers([]));
  }, []);
  const term = search.trim().toLowerCase();
  const shown = (users || []).filter((user) =>
    !term || [user.name, user.firm_name, user.email, user.phone_number].some((value) => (value || "").toLowerCase().includes(term))
  );
  return (
    <div className="qt-user-picker">
      <div className="qt-user-picker-head">
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search portal users" autoFocus />
        <button type="button" className="mb-text-button" onClick={onClose}>
          Cancel
        </button>
      </div>
      {users == null ? (
        <p className="ar-muted">Loading...</p>
      ) : (
        <ul className="ar-project-choices">
          {shown.slice(0, 40).map((user) => (
            <li key={user.user_id}>
              <button type="button" onClick={() => onPick(user)}>
                <strong>{user.firm_name || user.name}</strong>
                <span>{[user.firm_name ? user.name : null, user.phone_number, user.email].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
          {!shown.length && <p className="ar-muted">No matching users.</p>}
        </ul>
      )}
    </div>
  );
}

export default function CustomerDialog({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(customer || {}) });
  const [sameAddress, setSameAddress] = useState(!customer?.shipping_address);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const gstin = (form.gstin || "").trim().toUpperCase();
  const gstinBad = Boolean(gstin) && !GSTIN.test(gstin);
  const stateMismatch = GSTIN.test(gstin) && form.state_code && form.state_code !== gstin.slice(0, 2);

  const changeGstin = (event) => {
    const value = event.target.value.toUpperCase().replace(/\s/g, "");
    // A valid GSTIN carries its state in the first two digits.
    setForm((current) => ({ ...current, gstin: value, ...(GSTIN.test(value) ? { state_code: value.slice(0, 2) } : {}) }));
  };

  const fromUser = (user) => {
    setPicking(false);
    setForm((current) => ({
      ...current,
      name: user.name || current.name,
      company: user.firm_name || current.company,
      phone: user.phone_number || current.phone,
      email: user.email || current.email,
      billing_address: user.address || current.billing_address,
      source_user_id: user.user_id,
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const body = { ...form, gstin, shipping_address: sameAddress ? "" : form.shipping_address };
    try {
      const { data } = customer?.id
        ? await client.put(`/api/admin/customers/${customer.id}`, body)
        : await client.post("/api/admin/customers", body);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save the customer.");
      setBusy(false);
    }
  };

  return (
    <Modal title={customer?.id ? "Edit customer" : "New customer"} onClose={onClose} wide>
      {picking ? (
        <PortalUserPicker onPick={fromUser} onClose={() => setPicking(false)} />
      ) : (
        <form className="ar-form" onSubmit={save}>
          {!customer?.id && (
            <button type="button" className="mb-wide-button ar-fit" onClick={() => setPicking(true)}>
              <UserRoundSearch size={15} strokeWidth={1.8} aria-hidden="true" />
              Fill from a portal user
            </button>
          )}
          <div className="ar-grid-2">
            <label className="ar-field">
              <span className="ar-field-label">Contact name *</span>
              <input type="text" value={form.name} maxLength={160} onChange={set("name")} required autoFocus />
            </label>
            <label className="ar-field">
              <span className="ar-field-label">Company / firm</span>
              <input type="text" value={form.company || ""} maxLength={160} onChange={set("company")} />
            </label>
            <label className={`ar-field${gstinBad ? " is-invalid" : ""}`}>
              <span className="ar-field-label">GSTIN</span>
              <input type="text" value={form.gstin || ""} maxLength={15} onChange={changeGstin} placeholder="27ABCDE1234F1Z5" />
              {gstinBad && <span className="ar-field-error">15 characters, like 27ABCDE1234F1Z5</span>}
            </label>
            <label className={`ar-field${stateMismatch ? " is-invalid" : ""}`}>
              <span className="ar-field-label">State (place of supply)</span>
              <select value={form.state_code || ""} onChange={set("state_code")}>
                <option value="">Choose a state</option>
                {STATE_OPTIONS.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name} ({code})
                  </option>
                ))}
              </select>
              {stateMismatch && <span className="ar-field-error">The GSTIN belongs to state {gstin.slice(0, 2)}</span>}
            </label>
            <label className="ar-field">
              <span className="ar-field-label">Phone</span>
              <input type="text" value={form.phone || ""} maxLength={40} onChange={set("phone")} />
            </label>
            <label className="ar-field">
              <span className="ar-field-label">Email</span>
              <input type="email" value={form.email || ""} maxLength={160} onChange={set("email")} />
            </label>
          </div>
          <label className="ar-field">
            <span className="ar-field-label">Billing address</span>
            <textarea rows={2} value={form.billing_address || ""} maxLength={500} onChange={set("billing_address")} />
          </label>
          <label className="mb-toggle">
            <input type="checkbox" checked={sameAddress} onChange={(event) => setSameAddress(event.target.checked)} />
            <span>Ship to the billing address</span>
          </label>
          {!sameAddress && (
            <label className="ar-field">
              <span className="ar-field-label">Shipping address</span>
              <textarea rows={2} value={form.shipping_address || ""} maxLength={500} onChange={set("shipping_address")} />
            </label>
          )}
          <label className="ar-field">
            <span className="ar-field-label">Notes (only admins see these)</span>
            <textarea rows={2} value={form.notes || ""} maxLength={1000} onChange={set("notes")} />
          </label>
          {error && <p className="ar-warning">{error}</p>}
          <div className="mb-modal-actions">
            <button type="button" className="mb-text-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="mb-primary" disabled={busy || gstinBad || stateMismatch || !form.name.trim()}>
              {busy ? "Saving..." : "Save customer"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
